import { IconFont, pointer, Select } from '@byecode/ui'
import type { DataSourceAbstract, Field, FieldType, SelectDataSourceVariable, TypeInstanceMap } from '@lighthouse/core'
import React, { useMemo } from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import styled from 'styled-components'

import { innerTypeJoinOperatorMap } from '../../../../constants'
import { getFieldOptions } from '../../../../utils'
import { FieldTypeTag } from '../../../FieldTypeTag'
import { getDateVariableOption } from '../../help'
import type { VariableTree2 } from '../../types'
import { VariableSelect } from '../index'

const SCxContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 8px;
`

const SCxRemove = styled.div`
    ${pointer}
    padding: 2px;
    color: var(--color-gray-500);
    &:hover {
        color: var(--color-gray-900);
    }
`

const SelectStyle = {
    width: '289px',
    flexShrink: 0,
    overflow: 'hidden'
}
const disabledFields = new Set<FieldType>(['notes', 'video', 'file'])

export interface MatchConditionProps {
    prefixName: `selectDataSourceVariable.filter.expression.conditions.${number}`
    dataSource: DataSourceAbstract
    inputOption?: VariableTree2
    userOption?: VariableTree2
    pageOptions?: VariableTree2[]
    viewOptions?: VariableTree2[]
    upstreamOptions?: VariableTree2[]
    onRemove?: () => void
}

export const MatchCondition: React.FC<MatchConditionProps> = ({
    prefixName,
    dataSource,
    inputOption,
    userOption,
    pageOptions,
    viewOptions,
    upstreamOptions,
    onRemove
}) => {
    const { control, setValue } = useFormContext<SelectDataSourceVariable>()
    const leftFieldVariable = useWatch({
        control,
        name: `${prefixName}.idVariable`
    })

    const operator = useWatch({
        control,
        name: `${prefixName}.operator`
    })

    const leftField: Field | undefined = useMemo(() => {
        const { schema } = dataSource
        return schema?.[leftFieldVariable?.fieldIdVariable?.fieldId]
    }, [dataSource, leftFieldVariable?.fieldIdVariable?.fieldId])

    const operatorOptions = useMemo(() => {
        if (!leftField) {
            return []
        }
        const { innerType } = leftField
        if (!innerType) {
            return []
        }
        return innerTypeJoinOperatorMap[innerType]
    }, [leftField])
    const { schema, viewOptions: { tableProps = [] } = {} } = dataSource || {}

    const leftFieldOptions = useMemo(() => {
        return getFieldOptions(schema, tableProps)
            .map(item => ({ ...item, extra: <FieldTypeTag type={item.type} innerType={item.innerType} /> }))
            .filter(item => !disabledFields.has(item.type))
    }, [schema, tableProps])

    const rightInnerTypes: TypeInstanceMap[] | undefined = useMemo(() => {
        const leftInnerType = leftField?.innerType
        if (!leftInnerType) {
            return undefined
        }
        if (leftInnerType === 'ARRAY' && ['contains', 'notContains'].includes(operator)) {
            return ['TEXT', 'NUMBER', 'ARRAY']
        }
        return [leftInnerType]
    }, [leftField?.innerType, operator])

    const dateOption = useMemo(
        () => (leftField?.type === 'date' ? getDateVariableOption(['TODAY', 'TOMORROW', 'YESTERDAY']) : undefined),
        [leftField?.type]
    )

    return (
        <SCxContainer>
            <Controller
                control={control}
                name={`${prefixName}.idVariable.fieldIdVariable.fieldId`}
                render={({ field }) => (
                    <Select
                        styles={{
                            root: SelectStyle
                        }}
                        disabledPortal
                        searchable
                        dropdownWidth={282}
                        position="bottom-start"
                        value={field.value}
                        options={leftFieldOptions}
                        onChange={val => {
                            field.onChange(val)
                            // setValue(`${prefixName}.rightFieldId`, '')
                            setValue(`${prefixName}.right`, undefined)
                            setValue(`${prefixName}.operator`, '=')
                        }}
                    />
                )}
            />
            <Controller
                control={control}
                name={`${prefixName}.operator`}
                render={({ field }) => (
                    <Select
                        styles={{
                            root: {
                                flex: 1,
                                flexShrink: 0
                            }
                        }}
                        disabledPortal
                        disabled={!leftField}
                        value={field.value}
                        options={operatorOptions}
                        onChange={val => {
                            field.onChange(val)
                        }}
                    />
                )}
            />
            {!['isEmpty', 'isNotEmpty'].includes(operator) && (
                <Controller
                    control={control}
                    name={`${prefixName}.paramList.${0}`}
                    render={({ field }) => (
                        <VariableSelect
                            disabled={!leftField}
                            // withinPortal
                            withinPortal={false}
                            field={leftField}
                            innerTypes={rightInnerTypes}
                            value={field.value}
                            onChange={field.onChange}
                            pageOptions={pageOptions}
                            viewOptions={viewOptions}
                            inputOption={inputOption}
                            userOption={userOption}
                            dateOption={dateOption}
                            upstreamOptions={upstreamOptions}
                            style={SelectStyle}
                            disablePexels
                            width="100%"
                        />
                    )}
                />
            )}
            <SCxRemove onClick={onRemove}>
                <IconFont type="Close" size={16} />
            </SCxRemove>
        </SCxContainer>
    )
}
